import { useCallback, useContext, useState } from 'react'
import * as React from 'react'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import RadioInput from 'LuxKit/Radio/RadioInput'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import AssistChip from 'LuxKit/Chips/AssistChip'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineCalendarAltIcon from 'LuxKit/Icons/line/LineCalendarAltIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import TextLink from 'LuxKit/TextLink'
import Image from 'components/Common/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { FormattedMessage, useIntl } from 'react-intl'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import type { LeLiveRegistration } from 'leLive/api/leLive'
import styled from 'styled-components'
import { rem } from 'polished'

const CardContainer = styled.div`
  background-color: #F3F1EA;
  border-radius: ${rem(16)};
  padding: ${rem(28)};
  box-shadow: 0 ${rem(20)} ${rem(25)} rgba(0, 0, 0, 0.1);
`

const CardLogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${rem(9)} 0;
`

const SuccessBanner = styled(Sheet)`
  && { background-color: ${props => props.theme.palette.highlight.primary.normalBackground}; }
`

const ChipSelect = styled.div`
  position: relative;

  select {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;
  }
`

export type EventDay = 'saturday' | 'sunday'

const EVENT_DAY_LABELS: Record<EventDay, string> = {
  saturday: 'Saturday',
  sunday: 'Sunday',
}

export interface RegistrationFormValues {
  ticketQuantity: number
  eventDay: EventDay
  marketingOptIn: boolean
}

interface Props {
  onSubmit: (values: RegistrationFormValues) => Promise<void> | void
  isSubmitting: boolean
  isLoading?: boolean
  registration: LeLiveRegistration | undefined
  error?: string
}

function CardLogo() {
  return (
    <CSSBreakpoint min="desktop">
      <CardLogoWrapper>
        <Image
          id="f3tsulfa56cg8fntg87"
          alt="Luxury Escapes Live 2026"
          width={200}
          height={100}
          dpr={2}
          enhancedFormats
        />
      </CardLogoWrapper>
    </CSSBreakpoint>
  )
}

function getEventDayFromApi(apiDay: string): EventDay {
  return apiDay === 'DAY_ONE' ? 'saturday' : 'sunday'
}

function LeLiveRegistrationForm(props: Props) {
  const { onSubmit, isSubmitting, isLoading, registration, error } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)

  const [isEditing, setIsEditing] = useState(false)
  const [ticketQuantity, setTicketQuantity] = useState(1)
  const [eventDay, setEventDay] = useState<EventDay>('saturday')
  const [marketingOptIn, setMarketingOptIn] = useState(false)

  const handleQuantityChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setTicketQuantity(Number(e.currentTarget.value))
  }, [])

  const handleSaturdayChange = useCallback(() => {
    setEventDay('saturday')
  }, [])

  const handleSundayChange = useCallback(() => {
    setEventDay('sunday')
  }, [])

  const handleMarketingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMarketingOptIn(e.currentTarget.checked)
  }, [])

  const handleChange = useCallback(() => {
    if (registration) {
      setEventDay(getEventDayFromApi(registration.eventDay))
      setTicketQuantity(registration.ticketQuantity)
      setMarketingOptIn(registration.marketingOptIn)
    }
    setIsEditing(true)
  }, [registration])

  const handleSubmit = useCallback(async() => {
    if (!loggedIn) {
      const result = await showModal<AccountAccessModalResult>(
        <AccountAccessModal initialMode="leLiveLogin" />,
      )
      if (!result.loggedIn) return
    }
    try {
      await onSubmit({ ticketQuantity, eventDay, marketingOptIn })
      setIsEditing(false)
    } catch {
      // Parent sets submitError; keep form visible so the error banner is shown
    }
  }, [loggedIn, showModal, onSubmit, ticketQuantity, eventDay, marketingOptIn])

  const submitLabel = intl.formatMessage({
    id: 'lhjfEvzm5HsRBtoVaFiq',
    defaultMessage: 'Get tickets',
    description: 'Submit button label for LE Live registration form in the LE Live events section',
  })

  const submittingLabel = intl.formatMessage({
    id: 'CmoEyabgzFRLZeuOFTus',
    defaultMessage: 'Registering\u2026',
    description: 'Action button label displayed while LE Live form is submitting',
  })

  if (isLoading) {
    return (
      <CardContainer>
        <Group direction="vertical" gap={20}>
          <CardLogo />
          <LoadingBox height={20} borderRadius="S" />
          <LoadingBox height={20} borderRadius="S" />
          <LoadingBox height={20} borderRadius="S" />
          <LoadingBox height={48} borderRadius="M" />
          <LoadingBox height={48} borderRadius="M" />
          <LoadingBox height={48} borderRadius="L" />
        </Group>
      </CardContainer>
    )
  }

  if (registration && !isEditing) {
    const registeredDay = EVENT_DAY_LABELS[getEventDayFromApi(registration.eventDay)]

    return (
      <CardContainer>
        <Group direction="vertical" gap={20}>
          <CardLogo />

          <Group direction="vertical" gap={8}>
            <BodyTextBlock variant="medium" startIcon={<LineTicketIcon />}>
              {intl.formatMessage({
                id: 'LlSrmp8y4iHWCDUgy3y1',
                defaultMessage: 'FREE entry for members; $29 door entry',
                description: 'Ticket pricing info for LE Live event in the LE Live events section',
              })}
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<LineCalendarAltIcon />}>
              {intl.formatMessage({
                id: 'DWCIdwA0ifE47FzNmCN_',
                defaultMessage: 'Sat 16 May & Sun 17 May 2026',
                description: 'Event dates for LE Live in the LE Live events section',
              })}
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon />}>
              {intl.formatMessage({
                id: 'a_xhG3Gbc98p7AQH04eJ',
                defaultMessage: 'Royal Exhibition Building, Melbourne',
                description: 'Event location for LE Live in the LE Live events section',
              })}
            </BodyTextBlock>
          </Group>

          <SuccessBanner borderRadius="L" padding={16}>
            <Group direction="horizontal" gap={12} verticalAlign="start">
              <SolidCheckCircleIcon />
              <BodyText variant="medium">
                <FormattedMessage
                  id="cVIrwdkv935Ml_MSCQes"
                  defaultMessage="<strong>{firstName}, we've sent you {ticketQuantity, plural, one {# ticket} other {# tickets}} for {day}!</strong> Check your inbox for your Luxury Escapes Live email confirmation. <changeLink>Change</changeLink>"
                  description="Success message after LE Live registration with ticket count, day, confirmation instructions, and change link"
                  values={{
                    firstName: registration.firstName,
                    ticketQuantity: registration.ticketQuantity,
                    day: registeredDay,
                    strong: (chunks) => <strong>{chunks}</strong>,
                    changeLink: (chunks) => <TextLink onClick={handleChange} size="medium" weight="bold">{chunks}</TextLink>,
                  }}
                />
              </BodyText>
            </Group>
          </SuccessBanner>
        </Group>
      </CardContainer>
    )
  }

  return (
    <CardContainer id="registration">
      <Group direction="vertical" gap={20}>
        <CardLogo />
        <Group direction="vertical" gap={8}>
          <BodyTextBlock variant="medium" startIcon={<LineTicketIcon />}>
            {intl.formatMessage({
              id: 'LlSrmp8y4iHWCDUgy3y1',
              defaultMessage: 'FREE entry for members; $29 door entry',
              description: 'Ticket pricing info for LE Live event in the LE Live events section',
            })}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineCalendarAltIcon />}>
            {intl.formatMessage({
              id: 'DWCIdwA0ifE47FzNmCN_',
              defaultMessage: 'Sat 16 May & Sun 17 May 2026',
              description: 'Event dates for LE Live in the LE Live events section',
            })}
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineMapMarkerIcon />}>
            {intl.formatMessage({
              id: 'a_xhG3Gbc98p7AQH04eJ',
              defaultMessage: 'Royal Exhibition Building, Melbourne',
              description: 'Event location for LE Live in the LE Live events section',
            })}
          </BodyTextBlock>
        </Group>

        <Group direction="vertical" gap={12}>
          <Sheet background="neutral-eight" borderRadius="L" padding="8 16">
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'pjKXOwCVa2oWR4XZ9J5M',
                  defaultMessage: 'Number of tickets:',
                  description: 'Form field label for ticket quantity selector in the LE Live events section',
                })}
              </BodyText>
              <ChipSelect>
                <AssistChip
                  variant="outline"
                  size="medium"
                  endIcon={<LineAngleDownIcon />}
                >
                  {ticketQuantity}
                </AssistChip>
                <select
                  value={String(ticketQuantity)}
                  onChange={handleQuantityChange}
                  aria-label={intl.formatMessage({
                    id: 'KjEQ0-o_4BneJMNiycZ6',
                    defaultMessage: 'Number of tickets',
                    description: 'Accessibility aria-label for ticket quantity selector',
                  })}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                    <option key={n} value={String(n)}>{n}</option>
                  ))}
                </select>
              </ChipSelect>
            </Group>
          </Sheet>

          <Sheet background="neutral-eight" borderRadius="L" padding={16}>
            <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" wrap="wrap" gap={20}>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'xu5bMTrQHJ_D269E0zIT',
                  defaultMessage: 'Date:',
                  description: 'Form field label for event day selector in the LE Live events section',
                })}
              </BodyText>
              <Group direction="horizontal" gap={16} verticalAlign="center">
                <RadioInput
                  name="eventDay"
                  value="saturday"
                  checked={eventDay === 'saturday'}
                  onChange={handleSaturdayChange}
                >
                  {intl.formatMessage({
                    id: 'l9AeI55zyixH2HPvmIUk',
                    defaultMessage: 'Sat, 16 May',
                    description: 'Saturday option for event day in the LE Live events section',
                  })}
                </RadioInput>
                <RadioInput
                  name="eventDay"
                  value="sunday"
                  checked={eventDay === 'sunday'}
                  onChange={handleSundayChange}
                >
                  {intl.formatMessage({
                    id: 'sQ8DbjEND2ZXivGwVjLz',
                    defaultMessage: 'Sun, 17 May',
                    description: 'Sunday option for event day in the LE Live events section',
                  })}
                </RadioInput>
              </Group>
            </Group>
          </Sheet>

          <CheckboxInput
            checked={marketingOptIn}
            onChange={handleMarketingChange}
          >
            {intl.formatMessage({
              id: 'zsbR2R8pezsXcIH15cZo',
              defaultMessage: 'I\u2019d like to receive exclusive travel offers and updates from Luxury Escapes and partners.',
              description: 'Marketing opt-in checkbox label in the LE Live events section',
            })}
          </CheckboxInput>
        </Group>

        {error && (
          <MessageBanner kind="critical">
            {error}
          </MessageBanner>
        )}

        <TextButton
          kind="primary"
          size="large"
          fit="full-width"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? submittingLabel : submitLabel}
        </TextButton>

      </Group>
    </CardContainer>
  )
}

export default LeLiveRegistrationForm
